import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, X, Loader2, RotateCcw } from "lucide-react";
import { askAssistant, buildAssistantContext, UPGRADE_URL } from "./aiAssist.js";
import { billAlertCount } from "./billWatch.js";
import { syncConfigured } from "./supabaseClient.js";

const selectStyle = { background: "var(--card2)", color: "var(--ivory)", border: "1px solid var(--hair)" };
const SUGGESTIONS = ["Where did most of my money go this month?", "How does this month compare with last month?", "Where could I realistically cut back?"];

// A small round button at the bottom right that opens a chat popup. Bill drafting lives on the Recurring screen.
export default function AssistantFab({ active, session, onOpenBills, hidden = false, lifted = true }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [upgrade, setUpgrade] = useState(false);
  const end = useRef(null);
  useEffect(() => { if (open) end.current?.scrollIntoView({ block: "end" }); }, [messages, busy, open]);
  useEffect(() => { setMessages([]); setErr(""); }, [active?.id]);   // a different profile starts a fresh chat

  if (hidden || !active) return null;
  const alerts = billAlertCount(active.expenses || []);
  const bottom = `calc(env(safe-area-inset-bottom, 0px) + ${lifted ? 84 : 20}px)`;

  async function send(text) {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    if (!syncConfigured || !session) { setErr("Sign in (Settings, then Account & sync) to use the assistant."); return; }
    const next = [...messages, { role: "user", content: q }];
    setMessages(next); setInput(""); setBusy(true); setErr(""); setUpgrade(false);
    try {
      const reply = await askAssistant(next, buildAssistantContext(active), active.currency);
      setMessages([...next, { role: "assistant", content: reply }]);
    } catch (e) { setErr(e.message); setUpgrade(e.status === 402); }
    finally { setBusy(false); }
  }

  return (
    <>
      {open && (
        <div role="dialog" aria-label="AI assistant" className="fixed z-30 right-3 flex flex-col rounded-2xl shadow-2xl"
          style={{ bottom: `calc(${bottom} + 58px)`, width: "min(94vw, 380px)", height: "min(68vh, 520px)", background: "var(--card2)", border: "1px solid var(--hair)" }}>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-hair">
            <Sparkles size={15} className="text-brass" />
            <div className="text-sm font-semibold flex-1">Ask about your spending</div>
            {messages.length > 0 && <button onClick={() => { setMessages([]); setErr(""); }} aria-label="New chat" className="p-1"><RotateCcw size={15} className="text-ivory-dim" /></button>}
            <button onClick={() => setOpen(false)} aria-label="Close" className="p-1"><X size={17} className="text-ivory-dim" /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {!messages.length && (
              <>
                <div className="text-xs text-ivory-dim">Try asking</div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} className="rounded-full bg-card px-3 py-1.5 text-xs text-left">{s}</button>)}
                </div>
                {alerts > 0 && (
                  <button onClick={() => { setOpen(false); onOpenBills(); }} className="w-full text-left rounded-xl px-3 py-2.5 text-xs" style={{ background: "var(--brass-dim)" }}>
                    {alerts} bill{alerts > 1 ? "s" : ""} went up recently. <span className="underline text-brass font-medium">See bills and draft a message</span>
                  </button>
                )}
              </>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className="rounded-2xl px-3 py-2 text-sm max-w-[88%] whitespace-pre-wrap break-words" style={{ background: m.role === "user" ? "var(--brass-dim)" : "var(--card)" }}>{m.content}</div>
              </div>
            ))}
            {busy && <div className="flex items-center gap-2 text-xs text-ivory-dim"><Loader2 size={14} className="animate-spin" /> Thinking…</div>}
            {err && <div className="text-xs" style={{ color: "var(--rust)" }}>{err}{upgrade && UPGRADE_URL && <> <a href={UPGRADE_URL} target="_blank" rel="noopener noreferrer" className="underline text-brass">See the paid plan</a></>}</div>}
            <div ref={end} />
          </div>

          <div className="px-3 pb-3 pt-2 border-t border-hair">
            <div className="flex gap-2 items-end">
              <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder="Ask a question…" className="flex-1 rounded-xl px-3 py-2 text-sm resize-none" style={selectStyle} />
              <button onClick={() => send()} disabled={busy || !input.trim()} className="bg-gold-grad text-ink rounded-full p-2.5 disabled:opacity-40" aria-label="Send"><Send size={15} /></button>
            </div>
            <div className="text-[10px] text-ivory-dim mt-2 leading-snug">Sends a summary of your spending (totals, categories, merchants, bills) to Claude to answer. AI can be wrong and isn't tax, legal or financial advice.</div>
          </div>
        </div>
      )}

      <button onClick={() => setOpen((o) => !o)} aria-label={open ? "Close AI assistant" : "Open AI assistant"}
        className="fixed z-30 right-3 w-12 h-12 rounded-full bg-gold-grad text-ink shadow-xl flex items-center justify-center" style={{ bottom }}>
        {open ? <X size={20} /> : <Sparkles size={20} />}
        {!open && alerts > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: "var(--rust)", color: "#fff" }}>{alerts}</span>}
      </button>
    </>
  );
}
